/**
 * The quick policy used to play out simulated hands (for every seat, including the AI's own later
 * turns). It is the baseline AI's logic made deterministic on the simulation RNG, with a per-seat
 * knock eagerness so the Observant profile can model opponents it has watched.
 * It reads only its own hand and the visible middle.
 */
import type { Card } from "../cards.ts";
import { evaluate, compareHands, HandCategory, type HandRank } from "../handEval.ts";
import type { Action } from "../whiskey.ts";
import type { Rng } from "../rng.ts";
import type { RolloutPolicy, SimState } from "./sim.ts";

/** 0 = waits for a strong hand, 0.5 = baseline AI, 1 = knocks early. */
export function makeRolloutPolicy(eagerness = 0.5): RolloutPolicy {
  const tripsKnock = eagerness < 0.25 ? HandCategory.Straight : HandCategory.ThreeOfAKind;
  const pairKnockTurn = eagerness > 0.75 ? 0 : eagerness < 0.25 ? 3 : 1;
  return (s: SimState, seat: number, rng: Rng) => {
    const hand = s.hands[seat];
    const current = evaluate(hand);
    if (!s.middleRevealed) {
      if (current.category === HandCategory.Zipper && seat !== s.dealerSeat && s.turnsTaken[seat] === 0 && s.stock.length > 6 && rng.next() < 0.45)
        return { action: { type: "TAKE_MIDDLE" }, knockAfter: false };
      return { action: { type: "REVEAL" }, knockAfter: false };
    }
    const swap = bestSwap(hand, s.middle, current);
    // after a knock: the whole middle whenever it beats our best hand (it's the last turn)
    if (s.knockerSeat !== -1 && !s.justRevealed && compareHands(evaluate(s.middle), swap ? swap.rank : current) > 0)
      return { action: { type: "TAKE_MIDDLE" }, knockAfter: false };
    if (!s.justRevealed) {
      const mid = evaluate(s.middle);
      if (mid.category >= HandCategory.Straight && compareHands(mid, swap ? swap.rank : current) > 0)
        return { action: { type: "TAKE_MIDDLE" }, knockAfter: s.knockerSeat === -1 && mid.category >= tripsKnock };
    }
    if (s.knockerSeat !== -1) return { action: swap ? swap.action : { type: "KEEP" }, knockAfter: false };
    if (current.category >= tripsKnock) return { action: { type: "KNOCK" }, knockAfter: false };
    if (swap) return { action: swap.action, knockAfter: swap.rank.category >= tripsKnock };
    if (current.category >= HandCategory.TwoPair && s.turnsTaken[seat] >= pairKnockTurn) return { action: { type: "KNOCK" }, knockAfter: false };
    if ((s.stock.length > 0 || s.discard.length > 0) && s.turnsTaken[seat] < 6)
      return { action: { type: "BLIND", give: weakestCardIndex(hand) }, knockAfter: false };
    return { action: current.category >= HandCategory.Pair ? { type: "KNOCK" } : { type: "KEEP" }, knockAfter: false };
  };
}

export function bestSwap(hand: Card[], middle: Card[], current: HandRank): { action: Action; rank: HandRank } | null {
  let best: { give: number; take: number; rank: HandRank } | null = null;
  const trial = hand.slice();
  for (let ti = 0; ti < middle.length; ti++) {
    for (let gi = 0; gi < hand.length; gi++) {
      const keep = trial[gi];
      trial[gi] = middle[ti];
      const r = evaluate(trial);
      trial[gi] = keep;
      if (compareHands(r, current) > 0 && (!best || compareHands(r, best.rank) > 0)) best = { give: gi, take: ti, rank: r };
    }
  }
  return best ? { action: { type: "SWAP_ONE", give: best.give, take: best.take }, rank: best.rank } : null;
}

/** Lowest unpaired card (keeps pairs) — the baseline's discard choice. */
export function weakestCardIndex(hand: Card[]): number {
  const counts = new Map<number, number>();
  for (const c of hand) counts.set(c.rank, (counts.get(c.rank) ?? 0) + 1);
  let idx = 0, worst = Infinity;
  for (let i = 0; i < hand.length; i++) {
    const score = hand[i].rank + ((counts.get(hand[i].rank) ?? 0) > 1 ? 100 : 0);
    if (score < worst) { worst = score; idx = i; }
  }
  return idx;
}
